import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Phone, Check } from "lucide-react";
import { toast } from "sonner";

/** Dial codes: GCC and the wider Arab world first, then the rest of the world. */
export const DIAL_CODES = [
  { code: "966", label: "🇸🇦 +966" },
  { code: "971", label: "🇦🇪 +971" },
  { code: "965", label: "🇰🇼 +965" },
  { code: "974", label: "🇶🇦 +974" },
  { code: "973", label: "🇧🇭 +973" },
  { code: "968", label: "🇴🇲 +968" },
  { code: "962", label: "🇯🇴 +962" },
  { code: "961", label: "🇱🇧 +961" },
  { code: "963", label: "🇸🇾 +963" },
  { code: "964", label: "🇮🇶 +964" },
  { code: "967", label: "🇾🇪 +967" },
  { code: "970", label: "🇵🇸 +970" },
  { code: "20", label: "🇪🇬 +20" },
  { code: "212", label: "🇲🇦 +212" },
  { code: "213", label: "🇩🇿 +213" },
  { code: "216", label: "🇹🇳 +216" },
  { code: "218", label: "🇱🇾 +218" },
  { code: "249", label: "🇸🇩 +249" },
  { code: "222", label: "🇲🇷 +222" },
  { code: "252", label: "🇸🇴 +252" },
  { code: "253", label: "🇩🇯 +253" },
  { code: "269", label: "🇰🇲 +269" },
  { code: "90", label: "🇹🇷 +90" },
  { code: "98", label: "🇮🇷 +98" },
  { code: "93", label: "🇦🇫 +93" },
  { code: "92", label: "🇵🇰 +92" },
  { code: "91", label: "🇮🇳 +91" },
  { code: "880", label: "🇧🇩 +880" },
  { code: "94", label: "🇱🇰 +94" },
  { code: "977", label: "🇳🇵 +977" },
  { code: "960", label: "🇲🇻 +960" },
  { code: "1", label: "🇺🇸 +1" },
  { code: "44", label: "🇬🇧 +44" },
  { code: "33", label: "🇫🇷 +33" },
  { code: "49", label: "🇩🇪 +49" },
  { code: "39", label: "🇮🇹 +39" },
  { code: "34", label: "🇪🇸 +34" },
  { code: "351", label: "🇵🇹 +351" },
  { code: "31", label: "🇳🇱 +31" },
  { code: "32", label: "🇧🇪 +32" },
  { code: "41", label: "🇨🇭 +41" },
  { code: "43", label: "🇦🇹 +43" },
  { code: "46", label: "🇸🇪 +46" },
  { code: "47", label: "🇳🇴 +47" },
  { code: "45", label: "🇩🇰 +45" },
  { code: "358", label: "🇫🇮 +358" },
  { code: "353", label: "🇮🇪 +353" },
  { code: "30", label: "🇬🇷 +30" },
  { code: "48", label: "🇵🇱 +48" },
  { code: "420", label: "🇨🇿 +420" },
  { code: "421", label: "🇸🇰 +421" },
  { code: "36", label: "🇭🇺 +36" },
  { code: "40", label: "🇷🇴 +40" },
  { code: "359", label: "🇧🇬 +359" },
  { code: "385", label: "🇭🇷 +385" },
  { code: "386", label: "🇸🇮 +386" },
  { code: "381", label: "🇷🇸 +381" },
  { code: "380", label: "🇺🇦 +380" },
  { code: "7", label: "🇷🇺 +7" },
  { code: "994", label: "🇦🇿 +994" },
  { code: "995", label: "🇬🇪 +995" },
  { code: "374", label: "🇦🇲 +374" },
  { code: "996", label: "🇰🇬 +996" },
  { code: "998", label: "🇺🇿 +998" },
  { code: "992", label: "🇹🇯 +992" },
  { code: "993", label: "🇹🇲 +993" },
  { code: "77", label: "🇰🇿 +77" },
  { code: "86", label: "🇨🇳 +86" },
  { code: "81", label: "🇯🇵 +81" },
  { code: "82", label: "🇰🇷 +82" },
  { code: "852", label: "🇭🇰 +852" },
  { code: "853", label: "🇲🇴 +853" },
  { code: "886", label: "🇹🇼 +886" },
  { code: "65", label: "🇸🇬 +65" },
  { code: "60", label: "🇲🇾 +60" },
  { code: "62", label: "🇮🇩 +62" },
  { code: "66", label: "🇹🇭 +66" },
  { code: "84", label: "🇻🇳 +84" },
  { code: "63", label: "🇵🇭 +63" },
  { code: "95", label: "🇲🇲 +95" },
  { code: "855", label: "🇰🇭 +855" },
  { code: "856", label: "🇱🇦 +856" },
  { code: "673", label: "🇧🇳 +673" },
  { code: "61", label: "🇦🇺 +61" },
  { code: "64", label: "🇳🇿 +64" },
  { code: "27", label: "🇿🇦 +27" },
  { code: "234", label: "🇳🇬 +234" },
  { code: "254", label: "🇰🇪 +254" },
  { code: "255", label: "🇹🇿 +255" },
  { code: "256", label: "🇺🇬 +256" },
  { code: "233", label: "🇬🇭 +233" },
  { code: "251", label: "🇪🇹 +251" },
  { code: "250", label: "🇷🇼 +250" },
  { code: "237", label: "🇨🇲 +237" },
  { code: "225", label: "🇨🇮 +225" },
  { code: "221", label: "🇸🇳 +221" },
  { code: "220", label: "🇬🇲 +220" },
  { code: "260", label: "🇿🇲 +260" },
  { code: "263", label: "🇿🇼 +263" },
  { code: "265", label: "🇲🇼 +265" },
  { code: "258", label: "🇲🇿 +258" },
  { code: "267", label: "🇧🇼 +267" },
  { code: "264", label: "🇳🇦 +264" },
  { code: "230", label: "🇲🇺 +230" },
  { code: "52", label: "🇲🇽 +52" },
  { code: "55", label: "🇧🇷 +55" },
  { code: "54", label: "🇦🇷 +54" },
  { code: "56", label: "🇨🇱 +56" },
  { code: "57", label: "🇨🇴 +57" },
  { code: "51", label: "🇵🇪 +51" },
  { code: "58", label: "🇻🇪 +58" },
  { code: "593", label: "🇪🇨 +593" },
  { code: "591", label: "🇧🇴 +591" },
  { code: "595", label: "🇵🇾 +595" },
  { code: "598", label: "🇺🇾 +598" },
  { code: "506", label: "🇨🇷 +506" },
  { code: "507", label: "🇵🇦 +507" },
  { code: "502", label: "🇬🇹 +502" },
  { code: "504", label: "🇭🇳 +504" },
  { code: "503", label: "🇸🇻 +503" },
  { code: "505", label: "🇳🇮 +505" },
  { code: "53", label: "🇨🇺 +53" },
  { code: "509", label: "🇭🇹 +509" },
  { code: "592", label: "🇬🇾 +592" },
];

/** Split a stored `+966512…` back into a dial code + local number. */
export function splitPhone(stored) {
  const digits = String(stored || "").replace(/[^0-9]/g, "");
  if (!digits) return { country: "966", number: "" };
  const hit = DIAL_CODES.slice()
    .sort((a, b) => b.code.length - a.code.length)
    .find((c) => digits.startsWith(c.code));
  return hit
    ? { country: hit.code, number: digits.slice(hit.code.length) }
    : { country: "966", number: digits };
}

/**
 * PhoneCard — lets a member keep their own contact number current. Only the
 * admin console displays other people's numbers.
 */
export function PhoneCard() {
  const { t } = useTranslation();
  const { user, fetchMe } = useAuth();
  const initial = splitPhone(user?.phone);
  const [country, setCountry] = useState(initial.country);
  const [number, setNumber] = useState(initial.number);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await client.patch("/me/contact", { phone_country: country, phone: number });
      if (fetchMe) await fetchMe();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      toast.success(t("phone.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-2xl border border-border p-4 sm:p-5 space-y-3" data-testid="dashboard-phone-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Phone className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium">{t("phone.title")}</p>
          <p className="text-[11px] text-muted-foreground">{t("phone.hint")}</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="sm:w-[140px]">
          <Label htmlFor="phone-country" className="sr-only">{t("phone.country")}</Label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger id="phone-country" className="rounded-xl h-10" data-testid="dashboard-phone-country">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {DIAL_CODES.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          value={number}
          onChange={(e) => setNumber(e.target.value.replace(/[^0-9\s-]/g, ""))}
          placeholder="5X XXX XXXX"
          dir="ltr"
          inputMode="tel"
          className="rounded-xl h-10 flex-1 font-mono"
          data-testid="dashboard-phone-number"
        />
        <Button onClick={save} disabled={saving} className="btn-brand rounded-xl h-10 gap-2" data-testid="dashboard-phone-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : null}
          {t("common.save")}
        </Button>
      </div>
    </Card>
  );
}

export default PhoneCard;
